/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { or } from '@ember/object/computed';
import { isArray } from '@ember/array';
import { isPresent, isEmpty, typeOf } from '@ember/utils';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { getValueDefault } from '@linc-technologies/linced-common/utils/dynamic-field-types';
import {
  attachDraft,
  draftFieldOf,
  isDraft,
  isSettled,
  settleField,
} from '@linc-technologies/linced-common/utils/dynamic-fields';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import moment from 'moment';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import {
  compact,
  findBy,
  mapBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { classNameBindings, classNames } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';

// Template-less base holding all of DynamicField's behaviour.
// This exists to break an import cycle.
//
// dynamic-field.hbs statically invokes <Learner::IdSearchField>, and
// embroider's template-colocation plugin makes dynamic-field.js import its own
// colocated template:
//
// * dynamic-field.js -> dynamic-field.hbs.js -> learner/id-search-field.js -> dynamic-field.js
//
// This module has no colocated template, so nothing invoked from
// dynamic-field.hbs can reach back into it.
@classNames('layout-row', 'flex')
@classNameBindings('isDirty:md-dirty', 'isReadOnly:md-readonly')
export default class DynamicFieldBase extends Component {
  // Services
  @service('field-options')
  optionsService;

  @service
  store;

  // Attrs
  DataType = DataType;

  InputType = InputType;

  @computed(
    'defaultValue',
    'entity.{isNew,fieldsArray.[]}',
    'entityField.{hasDirtyAttributes,value}',
    'isReadOnly',
  )
  get isDirty() {
    if (this.isReadOnly) {
      // Don't render the field as "dirty" if it's read-only.
      return false;
    }
    if (isDraft(this.entity, this.entityField)) {
      // A draft shows the default, not a user change.
      return false;
    }
    if (this.entity.isNew) {
      // If the whole entity is new, the field isn't "dirty" unless its value
      // is different from the default.
      let entityValue = this.get('entityField.value');
      if (isEmpty(this.defaultValue) && isEmpty(entityValue)) {
        // If both are different flavours of empty, it's not dirty.
        return false;
      }
      if (isArray(this.defaultValue) && isArray(entityValue)) {
        // Convert to flat strings to compare since arrays are never strictly
        // equal to each other; entityValue isn't a plain array so needs to
        // be converted first to get the actual data.
        return (
          this.defaultValue.toString() !== entityValue.toArray().toString()
        );
      }
      return this.defaultValue !== entityValue;
    }
    // Otherwise, it's dirty if it's dirty.
    return this.get('entityField.hasDirtyAttributes');
  }

  // currentMedia contains the current media record.
  @tracked
  currentMedia = null;

  // stagedMedia contains the media record for input type == file.
  @tracked
  stagedMedia = null;

  // customValidations allows the caller to specify any additional validations.
  customValidations = null;

  min = null;
  max = null;

  // Props
  @tracked prefix = '';

  readOnly = false;
  required = false;
  forceOptional = false;

  // The form to register the rendered field with, so it counts towards the
  // form's validity.
  parentComponent = null;

  // State
  // isInitialised provides the field's initialization state.
  @tracked
  isInitialised = false;

  // canRevertMedia provides a check value for when media can be reverted to the
  // initial value.
  //
  // NOTE: deliberately not named `revertMedia` - that would collide with the
  // `revertMedia` action below, which is a sibling class member now that the
  // actions hash is gone.
  @tracked
  canRevertMedia = false;

  @computed('field.key', 'prefix')
  get fieldKey() {
    return this.prefix + this.get('field.key');
  }

  @computed('field.{inputType,key,options.[],optionsFromEnum}')
  get fieldOptions() {
    let typesWithOptions = [
      InputType.RADIO,
      InputType.CHECKBOX,
      InputType.SELECT,
      InputType.MULTI_SELECT,
      InputType.SORTABLE_LIST,
      InputType.WYSIWYG_EDITOR,
    ];
    if (typesWithOptions.includes(this.field.inputType)) {
      if (this.field.optionsFromEnum) {
        return this.optionsService.getOptions(this.fieldKey);
      } else {
        return this.field.options;
      }
    }
    return [];
  }

  // `selectedOptionOverride` lets callers override the computed default; Ember < 4
  // allowed that implicitly (computed-property.override).
  selectedOptionOverride = null;

  @computed(
    'fieldOptions.[]',
    'entityField.{datatype,value,valueInt}',
    'selectedOptionOverride',
  )
  get selectedOption() {
    if (this.selectedOptionOverride !== null) {
      return this.selectedOptionOverride;
    }

    let options = this.fieldOptions;
    if (isEmpty(this.entityField)) {
      return null;
    }
    if (this.entityField.get('datatype') === DataType.INTEGER) {
      if (options.length && !isArray(this.entityField.get('valueInt'))) {
        return findBy(options, 'valueInt', this.entityField.get('valueInt'));
      }
    } else {
      if (options.length && !isArray(this.entityField.get('value'))) {
        return findBy(options, 'value', this.entityField.get('value'));
      }
    }
    return null;
  }

  @computed('fieldOptions.[]', 'entityField.value.[]')
  get selectedOptions() {
    let options = this.fieldOptions;
    const selectedValues = this.get('entityField.value');
    let selectedOptions = [];
    if (isEmpty(this.entityField)) {
      return selectedOptions;
    }
    // Options are displayed in the order they are selected
    if (options.length && selectedValues.length) {
      selectedOptions = selectedValues
        .map((value) => {
          return options.find((option) => option.value === value);
        })
        .filter(Boolean);
    }
    return selectedOptions;
  }

  @computed('selectedOptions.[]', 'fieldOptions.[]')
  get remainingOptions() {
    let options = this.fieldOptions;
    let filtered = [];
    if (!isEmpty(options)) {
      filtered = options.filter((source) => {
        return !this.selectedOptions.some(function (option) {
          return source.value === option.value;
        });
      });
    }
    return filtered;
  }

  @computed('fieldOptions.[]', 'entityField.valueList.[]')
  get sortedOptions() {
    let valueList = this.get('entityField.valueList') ?? [];
    // Grab any field options which aren't yet selected, so we can add them.
    let newOptions = this.fieldOptions.filter(
      (option) => !valueList.includes(option.value),
    );
    let options = compact(
      valueList.reduce((sorted, value) => {
        sorted.push(findBy(this.fieldOptions, 'value', value));
        return sorted;
      }, []),
    );
    return [...options, ...newOptions];
  }

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @computed('field.inputType')
  get inputType() {
    switch (this.get('field.inputType')) {
      case InputType.TEXT:
        return 'text';
      case InputType.DATE:
        return 'date';
      case InputType.EMAIL:
        return 'email';
      case InputType.NUMBER:
        return 'number';
      case InputType.URL:
        return 'url';
      case InputType.COLOR:
        return 'color';
      case InputType.WYSIWYG:
        return 'wysiwyg';
    }

    return '';
  }

  @or('readOnly', 'field.isReadOnly')
  isReadOnly;

  @computed('required', 'field.isRequired', 'forceOptional')
  get isRequired() {
    if (this.forceOptional) {
      // The `forceOptional` flag overrides any required flags.
      return false;
    }
    return this.required || this.field.isRequired;
  }

  dateValidator = Object.freeze([
    {
      message: 'Please enter a valid date in YYYY-MM-DD format',
      validate: (val) => {
        return (
          isEmpty(val) ||
          (/^\d{4}-\d{2}-\d{2}$/.test(val) &&
            moment(val, 'YYYY-MM-DD', true).isValid())
        );
      },
    },
  ]);

  // filteredValidations combines all applicable custom validations for this
  // field, falling back to an empty array as `null` is not a valid value.
  @computed('customValidations.[]', 'field.inputType')
  get filteredValidations() {
    if (this.field.inputType === InputType.DATE) {
      return compact([].concat(this.dateValidator, this.customValidations));
    } else if (!isEmpty(this.customValidations)) {
      return this.customValidations;
    }
    return [];
  }

  @observes('entity.id')
  _entityChanged() {
    this.isInitialised = false;
    this.initValue();
  }

  @observes('field.key')
  hasFieldMeta() {
    if (!isEmpty(this.get('field.key'))) {
      this.initValue();
    }
  }

  @computed('field.placeholder', 'selectedOptions.length')
  get chipsPlaceholder() {
    if (this.get('selectedOptions.length') > 0) {
      return this.get('field.placeholder');
    }
    return '';
  }

  matchOption(option, term) {
    let text = stripDiacritics(term).toLowerCase();
    let labelMatch = stripDiacritics(option.label).toLowerCase().indexOf(text);
    let valueMatch = stripDiacritics(option.value).toLowerCase().indexOf(text);
    return Math.max(labelMatch, valueMatch);
  }

  // entityField is the value fragment that the form shows and edits. When the
  // entity has no such field, or the field is empty and the schema has a
  // default, it is a draft: a fragment with the default value that is not in
  // the entity yet. So opening a form does not make the entity dirty. A user
  // edit attaches the draft (see editableField), and a save attaches each
  // draft with a default (see the store saveRecord).
  @computed('fieldKey', 'entity.{id,fieldsArray.@each.key}')
  get entityField() {
    return this.getEntityField();
  }

  @tracked
  newItemLabel;

  @tracked
  newItemValue;

  @action
  setNewItemValue(value) {
    this.newItemValue = value;
  }

  getEntityField() {
    let field = this.get('entity.fields').get(this.fieldKey);
    if (field && isSettled(this.entity, this.fieldKey)) {
      // A user or a caller set the field. Show it as it is, also when it is
      // empty (a user cleared it).
      return field;
    }
    let initialValue = this.defaultValue;
    if (this.get('field.inputType') === InputType.SORTABLE_LIST) {
      // Add the options that are not in the list yet to the end of the list.
      let valueList = field?.valueList ?? [];
      initialValue = [
        ...valueList.filter((value) =>
          findBy(this.fieldOptions, 'value', value),
        ),
        ...mapBy(
          this.fieldOptions.filter(
            (option) => !valueList.includes(option.value),
          ),
          'value',
        ),
      ];
      if (field && initialValue.join() === valueList.join()) {
        return field;
      }
    } else if (field && (isPresent(field.value) || isEmpty(initialValue))) {
      return field;
    }

    return draftFieldOf(
      this.entity,
      this.fieldKey,
      this.get('field.dataType'),
      initialValue,
      this.get('field.requiredRole'),
    );
  }

  // editableField gives entityField for a user edit. A draft is attached to
  // the entity first, so the edit makes the entity dirty and is saved.
  editableField() {
    let field = this.entityField;
    if (isDraft(this.entity, field)) {
      attachDraft(this.entity, this.fieldKey);
    }
    settleField(this.entity, this.fieldKey);
    return field;
  }

  @action
  setValue(value) {
    this.editableField().set('value', value);
  }

  getEntityValue() {
    return this.getEntityField().get('value');
  }

  // `defaultValueOverride` lets callers override the computed default; Ember < 4
  // allowed that implicitly (computed-property.override).
  defaultValueOverride = null;

  @computed('field.{default.value,dataType}', 'defaultValueOverride')
  get defaultValue() {
    if (this.defaultValueOverride !== null) {
      return this.defaultValueOverride;
    }

    let defaultValue = this.get('field.default.value');
    let dataType = this.get('field.dataType');
    if (isEmpty(defaultValue)) {
      defaultValue = getValueDefault(dataType);
    }
    if (dataType === DataType.LIST) {
      if (!isEmpty(defaultValue) && !isArray(defaultValue)) {
        // Field's datatype is `list`; split on comma
        defaultValue = defaultValue.split(',');
      }
      // If it's an EmberArray, unwrap it to a native array.
      if (typeOf(defaultValue.toArray) === 'function') {
        defaultValue = [...defaultValue];
      }
    }

    if (dataType === DataType.VALUE_LIST) {
      defaultValue = defaultValue.map((fragment) => {
        return fragment.data;
      });
    }
    return defaultValue;
  }

  init() {
    super.init(...arguments);
    // Trigger meta check so that value is initialized if model already loaded
    next(this, this.hasFieldMeta);
  }

  initValue() {
    if (this.isInitialised) {
      return;
    }

    let inputType = this.get('field.inputType');
    let entityValue = this.getEntityValue();
    if (inputType === InputType.FILE && !isEmpty(entityValue)) {
      return this.store
        .findRecord('media', entityValue)
        .then((media) => {
          this.currentMedia = media;
          this.stagedMedia = media;
        })
        .finally(() => {
          this.isInitialised = true;
        });
    }

    // ensure field is only marked as initialised once the media record
    // has been retrieved successfully, or in error...
    this.isInitialised = true;
  }

  // _clearMediaValues removes the staged media record and field value.
  _clearMediaValues() {
    this.editableField().set('value', '');
    this.stagedMedia = null;
  }

  @action
  removeOption(item) {
    this.editableField().get('value').removeObject(item.value);
  }

  @action
  addOption(item) {
    this.editableField().get('value').pushObject(item.value);
  }

  @action
  selectOption(item) {
    let field = this.editableField();
    if (field.get('datatype') === DataType.INTEGER) {
      field.set('valueInt', isEmpty(item) ? null : parseInt(item.valueInt, 10));
    } else {
      field.set('value', isEmpty(item) ? null : item.value);
    }
  }

  @action
  toggleChecked(value) {
    let checked = this.editableField().get('value');
    if (checked.includes(value)) {
      checked.removeObject(value);
    } else {
      checked.pushObject(value);
    }
  }

  @action
  maybeMutBoth(a, b, context, newValue) {
    if (context.get(a) === context.get(b)) {
      context.set(b, newValue);
    }
    context.set(a, newValue);
  }

  @action
  addValueListItem() {
    this.editableField().get('value').pushObject({
      value: this.newItemValue,
      label: this.newItemLabel,
    });
    this.newItemValue = '';
    this.newItemLabel = '';
  }

  @action
  removeValueListItem(item) {
    this.editableField().get('value').removeObject(item);
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    if (sourceList === targetList) {
      targetList.splice(targetIndex, 0, item);
    } else {
      // Fields are fragments, so if the list changes they must be copied
      // as fragments' owners can't be changed by simply moving the fragment over.
      targetList.splice(targetIndex, 0, item.copy());
    }

    if (this.get('field.inputType') === InputType.SORTABLE_LIST) {
      // Sortable Lists need their value updated, as it's decoupled from the
      // UI (the value is a list of strings, not objects).
      next(() => {
        this.editableField().set(
          'valueList',
          mapBy(this.sortedOptions, 'value'),
        );
      });
    }
  }

  // onUploadStart implements media-picker's onUploadStart.
  @action
  onUploadStart() {
    this.canRevertMedia = false;
  }

  // onUploadFinished implements media-picker's onUploadFinished.
  @action
  onUploadFinished(media) {
    this.stagedMedia = media;
    this.editableField().set('value', media.id);
  }

  // onUploadRemove implements media-picker's onUploadRemove.
  @action
  onUploadRemove() {
    let currentMedia = this.currentMedia;
    let stagedMedia = this.stagedMedia;

    // case: no existing media.
    if (isEmpty(currentMedia)) {
      return this.stagedMedia
        .destroyRecord()
        .then(() => this._clearMediaValues());
    }

    // case: current media being removed.
    if (currentMedia.id === stagedMedia.id) {
      this._clearMediaValues();
      this.canRevertMedia = true;
      return;
    }

    // case: different media uploaded, but removed, therefore revert back.
    return this.stagedMedia.destroyRecord().then(() => {
      this.entityField.rollbackAttributes();
      this.stagedMedia = currentMedia;
    });
  }

  // revertMedia returns to the initial uploaded media.
  @action
  revertMedia() {
    let currentMedia = this.currentMedia;
    this.stagedMedia = currentMedia;
    this.editableField().set('value', currentMedia.id);
    this.canRevertMedia = false;
  }
}
